import React from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { reorder } from '../../helpers';
import CustomFieldVM from './custom-field.viewmodel';
import { CustomFieldDefault } from './fields/custom-field-default';
import { CustomFieldName } from './fields/custom-field-name';
import { CustomFieldHidden } from './fields/custom-field-hidden';
import { CustomFieldCreateForm } from './custom-field-create-form';
import './custom-field.scss';
import TriagedQuestionForm from './triaged-question/triaged-question-form';
import { ICustomFieldsDTO } from '../../../../../../../api/custom-fields.dao';
import RemoveEntityButton from '../../../../../../global/remove-entity-button/remove-entity-button';
import { ProjectCSPNonceDerictives } from '../../../../../../../constants/common.constants';

interface IWebsiteCustomFieldPresentation {
    vm: CustomFieldVM;
    intl: IntlShape;
}

export const CustomRadioValues = {
    optional: 'Optional',
    required: 'Required'
};

export const ALLOWED_CUSTOM_FIELDS_QUANTITY = 25;
export const MAX_COUNT_ANSWERS = 25;

const CustomFieldView = injectIntl(mobxReact.observer(({ vm , intl }: IWebsiteCustomFieldPresentation) => {
    const { fields } = vm;
    const [sortableFields, staticFields] = vm.getOrderedFields().reduce((prev, next) => {
        prev[vm.isFieldVisible(next) ? 0 : 1].push(next);
        return prev;
    }, [[], []]);

    const onDragEnd = mobx.action(result => {
        if (!result.destination) {
            return;
        }

        const sorted = reorder(
            sortableFields,
            result.source.index,
            result.destination.index
        );

        vm.updateOrder(sorted.concat(staticFields) as ICustomFieldsDTO[]);
    });

    const toggleVisibility = mobx.action((id, currentField) => {
        const visible = vm.isFieldVisible(currentField);

        if (visible) {
            vm.setFieldAsHidden(currentField);
        } else {
            vm.setFieldAsVisible(currentField);
        }

        const updatedFields = vm.isFieldVisible(currentField) ?
            sortableFields.concat(currentField, staticFields.filter(field => (field.customFieldId ?? field.defaultFieldId) !== id)) :
            fields.filter(field => (field.customFieldId ?? field.defaultFieldId) !== id).concat(currentField);

        vm.updateOrder(updatedFields);
    });

    return vm && (
        <div className='oe-form oe-custom-field'>
            <div className='oe-form__body'>
                {vm.shouldShowTriagedQuestionForm
                ? <TriagedQuestionForm
                    vm={vm}
                />
                : <><DragDropContext onDragEnd={onDragEnd} nonce={ProjectCSPNonceDerictives.OEC_DRAG_DROP_STYLES}>
                <Droppable droppableId='droppable'>
                    {droppableProps => (
                        <div
                        {...droppableProps.droppableProps}
                        ref={droppableProps.innerRef}>
                        {sortableFields.map((field, index) => (
                            <Draggable
                                key={field.customFieldId ? `custom_${field.customFieldId}` : `default_${field.defaultFieldId}`}
                                draggableId={field.customFieldId ? `custom_${field.customFieldId}` : `default_${field.defaultFieldId}`}
                                index={index}>
                                {draggableProps => {
                                    return (
                                        <div
                                            className='custom-field__wrapper'
                                            ref={draggableProps.innerRef}
                                            {...draggableProps.draggableProps}>
                                            { field.type ?
                                                <CustomFieldDefault
                                                    field={field}
                                                    vm={vm}
                                                    updateField={vm.updateField.bind(vm)}
                                                    toggleVisibility={toggleVisibility.bind(vm)}
                                                    getFieldErrorMessage={vm.getFieldErrorMessage.bind(vm)}
                                                    combineOptionsArrayIntoString={vm.combineOptionsArrayIntoString.bind(vm)}
                                                    getOptionsFieldErrorMessage={vm.getOptionsFieldErrorMessage.bind(vm)}
                                                    getShortRichTextValidationError={vm.getShortRichTextLengthError.bind(vm)}
                                                    getOptionsArray={vm.getOptionsArray.bind(vm)}
                                                    key={vm.getFieldOrder(field)}
                                                    getShortPlainValidationError={vm.getShortPlainValidationError.bind(vm)}
                                                />
                                                :
                                                <CustomFieldName
                                                    field={field}
                                                    toggleVisibility={toggleVisibility.bind(vm)}
                                                    updateField={vm.updateField.bind(vm)}
                                                    key={vm.getFieldOrder(field)}
                                                    getFieldErrorMessage={vm.getFieldErrorMessage.bind(vm)}
                                                    vm={vm}
                                                />
                                            }
                                            <div className='custom-field__drag-icon'><i {...draggableProps.dragHandleProps} tabIndex={-1}/></div>
                                            {field.customFieldId &&
                                            <RemoveEntityButton
                                                classname='m-left-30px'
                                                onClick={() => vm.onRemoveCustomField(field.customFieldId, intl)}
                                            />}
                                        </div>
                                    );
                                }}
                            </Draggable>
                        ))}
                        {droppableProps.placeholder}
                        </div>
                    )}
                    </Droppable>
                </DragDropContext>

                {staticFields.map(field =>
                    <CustomFieldHidden field={field} toggleVisibility={toggleVisibility} key={vm.getFieldOrder(field)} />
                )}</>
                }

            </div>


            <CustomFieldCreateForm
                vm={vm}
                fields={fields}
                newField={vm.newField}
                isOpen={vm.isAddFormOpen}
                isValidField={vm.allowToShowCreateField.bind(vm)}
                updateOrder={vm.updateOrder.bind(vm)}
                cleanNewForm={vm.cleanNewForm.bind(vm)}
                getShortPlainValidationError={vm.getShortPlainValidationError.bind(vm)}
                getShortRichTextValidationError={vm.getShortRichTextLengthError.bind(vm)}
                toggleAddForm={vm.toggleAddForm.bind(vm)}
                combineOptionsArrayIntoString={vm.combineOptionsArrayIntoString.bind(vm)}
                getOptionsArray={vm.getOptionsArray.bind(vm)}
                showValidationErrors={vm.getFieldErrorMessage.bind(vm)}
                isExternalFieldIDChanged={vm.isExternalFieldIDChanged}
                isLabelChanged={vm.isLabelChanged}
                setTouchFieldStatus={vm.setTouchFieldStatus.bind(vm)}
            />

            {!vm.shouldShowTriagedQuestionForm && <DefaultButton
                className='w-100 m-top-15px website-custom__add-button'
                modifier={['link']}
                text={<FormattedMessage
                    id='oe.common.addCustomField'
                    defaultMessage='{prefix}Add Custom Field'
                    description='Button text'
                    values={{
                        prefix: '+ '
                    }}
                />}
                onClick={() => vm.toggleAddForm(true)}
                disabled={vm.checkAllowedCustomFieldsQuantity()}
            />}

        </div>


    );
}));

export { CustomFieldView };
